// W-96c0e692 (b), W-fa19322f: the date, time, weekday and zone where the
// person is, on every World message, for every engine and every route. The
// Desk runs on the person's Mac, so its zone is theirs (ProviderService
// sendTurn); a conversation kept in the World gets the zone its message
// carries, or the person's profile zone (backend/convex/lib/cleoTurnPlan.js).
// Without it a relative request ("tomorrow at 9", "next Tuesday") cannot be
// placed, and no engine is told the time of day or the weekday.
// W-57ca754b: the { dateTime, timeZone } shape is for tool calls; Cleo wrote
// it to the person in a spend answer when the line did not say so.
// Dependency-free: the World (backend/convex/lib/cleoTurnPlan.js), the Desk
// (apps/server workingContext.ts) and the web app share it.

/** The clock line as the Desk says it: the weekday, date, time and zone where the person is, and how times are given and read. */
export function worldClockPrompt(now: number, timeZone: string): string {
  const reading = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "shortOffset",
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    reading.find((item) => item.type === type)?.value ?? "";
  return `Now, where the person is: ${part("weekday")}, ${part("month")} ${part("day")}, ${part("year")}, ${part("hour")}:${part("minute")} ${part("dayPeriod")} in ${timeZone} (${part("timeZoneName")}). In tool calls, give and read times as a local time in a named zone, { dateTime, timeZone }, in this zone unless the person names another. To the person, write a time in plain words ("2:10 PM", "Wednesday, October 7 at 9:00 AM"), never in that shape or as milliseconds.`;
}

/**
 * The clock line when no screen reported the person's zone and their profile holds none (a text
 * line's message, a profile never set): the time in UTC, said as such, and the rule not to guess.
 */
export function worldClockPromptUnknownZone(now: number): string {
  const reading = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    reading.find((item) => item.type === type)?.value ?? "";
  return `Now: ${part("weekday")}, ${part("month")} ${part("day")}, ${part("year")}, ${part("hour")}:${part("minute")} ${part("dayPeriod")} UTC. The person's own time zone is not known here: before saving or reading a time for them, ask which zone they mean (or use the one they name), and in tool calls give and read times as a local time in a named zone, { dateTime, timeZone }. To the person, write a time in plain words, never in that shape or as milliseconds.`;
}

/** The zone a browser or app reports for the person, as an IANA name, or null when it reports none usable. */
export function reportedTimeZone(): string | null {
  try {
    const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === "string" && zone.length > 0 && zone.length <= 128 ? zone : null;
  } catch {
    return null;
  }
}
